/**
 * Subcontractor Dashboard
 * Shows overview of the subcontractor's assignments
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks, getWeek, isWithinInterval } from 'date-fns'
import { sv } from 'date-fns/locale'
import { getTgWorkStatusName, getTgWorkStatusColor } from '../../services/statusSystemV2'
import type { TgWorkStatus } from '../../types/statusTypes'
import { usePageTracking } from '../../hooks/usePageTracking'
import { needsHandbookConfirmation, getLatestHandbookVersion } from '../../services/handbook'
import { NewsWidget } from '../../components/NewsWidget'
import {
  Clock,
  Calendar,
  ArrowRight,
  Loader2,
  Leaf,
  AlertTriangle,
  BookOpen,
  FileText,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Navigation,
  Phone,
  X
} from 'lucide-react'

// Statuses visible to subcontractors (V2 statuses)
const VISIBLE_STATUSES = [
  'planerad',      // Upcoming confirmed assignments
  'pagaende',      // In progress - needs reporting
  'granskning',    // Waiting for admin review
  'attesterad',    // Approved
  'slutford'       // Completed
]

interface Coworker {
  id: string
  name: string
  phone: string | null
  color: string | null
}

interface Assignment {
  id: string
  assignment_number: number | null
  title: string
  start_date: string
  start_time: string | null
  end_time: string | null
  status: string
  address: string | null
  coworkers: Coworker[]
  tg_status: string | null
}

export function SubcontractorDashboard() {
  usePageTracking('dashboard')

  const navigate = useNavigate()
  const { user } = useAuth()
  const [selectedWeekStart, setSelectedWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  )
  const [selectedCoworker, setSelectedCoworker] = useState<Coworker | null>(null)
  const [showAllWeekAssignments, setShowAllWeekAssignments] = useState(false)

  const MAX_WEEK_ASSIGNMENTS = 5

  // Fetch subcontractor ID from user_id
  const { data: subcontractor, isLoading: subLoading } = useQuery({
    queryKey: ['my-subcontractor', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      const { data, error } = await supabase
        .from('subcontractors')
        .select('id, name')
        .eq('user_id', user.id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!user?.id
  })

  // Fetch all assignments
  const { data: assignments = [], isLoading: assignmentsLoading } = useQuery<Assignment[]>({
    queryKey: ['subcontractor-all-assignments', subcontractor?.id],
    queryFn: async () => {
      if (!subcontractor?.id) return []

      const { data, error } = await supabase
        .from('assignments')
        .select(`
          id,
          assignment_number,
          title,
          start_date,
          start_time,
          end_time,
          status,
          location,
          assignment_subcontractors!inner (subcontractor_id, tg_status)
        `)
        .eq('assignment_subcontractors.subcontractor_id', subcontractor.id)
        .neq('assignment_subcontractors.tg_status', 'avsagt')
        .neq('assignment_subcontractors.tg_status', 'preliminar_tilldelad')
        .in('status', VISIBLE_STATUSES)
        .order('start_date')
        .order('start_time')

      if (error) throw error

      const assignmentIds = (data || []).map(a => a.id)

      // Fetch all coworkers (other TGs) for these assignments (exclude declined)
      const coworkersByAssignment: Record<string, Coworker[]> = {}
      if (assignmentIds.length > 0) {
        const { data: coworkerData } = await supabase
          .from('assignment_subcontractors')
          .select(`
            assignment_id,
            subcontractors (id, name, phone, color)
          `)
          .in('assignment_id', assignmentIds)
          .neq('subcontractor_id', subcontractor.id)
          .neq('tg_status', 'avsagt')

        for (const row of coworkerData || []) {
          const sub = row.subcontractors as any
          if (sub) {
            if (!coworkersByAssignment[row.assignment_id]) {
              coworkersByAssignment[row.assignment_id] = []
            }
            coworkersByAssignment[row.assignment_id].push({
              id: sub.id,
              name: sub.name,
              phone: sub.phone,
              color: sub.color
            })
          }
        }
      }

      return (data || []).map(a => {
        const tgData = (a.assignment_subcontractors as any)?.[0] || {}

        return {
          id: a.id,
          assignment_number: a.assignment_number,
          title: a.title,
          start_date: a.start_date,
          start_time: a.start_time,
          end_time: a.end_time,
          status: a.status,
          address: typeof a.location === 'string' ? a.location : (a.location as any)?.address || null,
          coworkers: coworkersByAssignment[a.id] || [],
          tg_status: tgData.tg_status || null
        }
      })
    },
    enabled: !!subcontractor?.id
  })

  // Week navigation
  const selectedWeekEnd = endOfWeek(selectedWeekStart, { weekStartsOn: 1 })
  const selectedWeekNumber = getWeek(selectedWeekStart, { weekStartsOn: 1 })
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
  const isCurrentWeek = selectedWeekStart.getTime() === currentWeekStart.getTime()

  const goToPrevWeek = () => {
    setSelectedWeekStart(prev => subWeeks(prev, 1))
    setShowAllWeekAssignments(false)
  }
  const goToNextWeek = () => {
    setSelectedWeekStart(prev => addWeeks(prev, 1))
    setShowAllWeekAssignments(false)
  }
  const goToCurrentWeek = () => {
    setSelectedWeekStart(currentWeekStart)
    setShowAllWeekAssignments(false)
  }

  // Get assignments for selected week
  const allWeekAssignments = assignments
    .filter(a => {
      const date = parseISO(a.start_date)
      return isWithinInterval(date, { start: selectedWeekStart, end: selectedWeekEnd })
    })

  const weekAssignments = showAllWeekAssignments
    ? allWeekAssignments
    : allWeekAssignments.slice(0, MAX_WEEK_ASSIGNMENTS)

  // Get assignments needing reporting - only ej_rapporterad (assignment done, needs report)
  const toReportAssignments = assignments
    .filter(a => a.tg_status === 'ej_rapporterad')
    .slice(0, 5)

  // Check if handbook confirmation is needed
  const { data: handbookStatus } = useQuery({
    queryKey: ['handbook-needs-confirmation', subcontractor?.id],
    queryFn: () => needsHandbookConfirmation(subcontractor!.id),
    enabled: !!subcontractor?.id
  })

  // Get latest handbook version for description
  const { data: latestVersion } = useQuery({
    queryKey: ['handbook-latest-version'],
    queryFn: getLatestHandbookVersion,
    enabled: handbookStatus?.needsConfirmation === true
  })

  const isLoading = subLoading || assignmentsLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">
          Välkommen{subcontractor?.name ? `, ${subcontractor.name}` : ''}!
        </h1>
        <p className="text-nature-600 dark:text-nature-400">
          Översikt över dina uppdrag
        </p>
      </div>

      {/* Handbook Confirmation Warning */}
      {handbookStatus?.needsConfirmation && (
        <Link
          to="/subcontractor/handbook"
          className="block bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-amber-900 dark:text-amber-100">
                {handbookStatus.confirmedVersion
                  ? 'Handboken har uppdaterats'
                  : 'Läs och bekräfta handboken'
                }
              </h3>
              {latestVersion?.description && handbookStatus.confirmedVersion && (
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  {latestVersion.description}
                </p>
              )}
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Klicka här för att läsa rutinerna och bekräfta att du tagit del av dem.
              </p>
            </div>
            <BookOpen className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
        </Link>
      )}

      {/* News Widget */}
      {subcontractor?.id && (
        <NewsWidget subcontractorId={subcontractor.id} maxItems={3} />
      )}

      {/* Assignment lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Week assignments */}
        <div className="bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary-600 flex-shrink-0" />
              <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                Uppdrag
              </h2>
              {allWeekAssignments.length > 0 && (
                <span className="text-sm font-normal text-nature-500">({allWeekAssignments.length})</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <button
                  onClick={goToPrevWeek}
                  className="p-1 rounded hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-nature-600 dark:text-nature-400" />
                </button>
                <span className="text-sm font-medium text-nature-700 dark:text-nature-300 min-w-[60px] text-center">
                  Vecka {selectedWeekNumber}
                </span>
                <button
                  onClick={goToNextWeek}
                  className="p-1 rounded hover:bg-nature-100 dark:hover:bg-nature-700 transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-nature-600 dark:text-nature-400" />
                </button>
              </div>
              {!isCurrentWeek && (
                <button
                  onClick={goToCurrentWeek}
                  className="px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50"
                >
                  Idag
                </button>
              )}
              <button
                onClick={() => navigate('/subcontractor/calendar', { state: { initialDate: selectedWeekStart } })}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Kalender <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Week date range */}
          <p className="text-xs text-nature-500 dark:text-nature-400 mb-3">
            {format(selectedWeekStart, 'yyyy-MM-dd')} – {format(selectedWeekEnd, 'yyyy-MM-dd')}
          </p>

          {weekAssignments.length === 0 ? (
            <p className="text-nature-500 dark:text-nature-400 text-sm py-4 text-center">
              Inga uppdrag denna vecka
            </p>
          ) : (
            <div className="space-y-3">
              {weekAssignments.map(a => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/subcontractor/assignments/${a.id}`)}
                  className="w-full text-left p-3 rounded-lg border border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      {a.assignment_number && (
                        <span className="text-xs font-mono text-primary-600 dark:text-primary-400">
                          #{a.assignment_number}
                        </span>
                      )}
                      <h4 className="font-medium text-nature-900 dark:text-nature-100 line-clamp-1">
                        {a.title}
                      </h4>
                      {a.address && (
                        <div className="text-sm text-nature-500 dark:text-nature-400 flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="line-clamp-1">{a.address}</span>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-1 p-1 rounded hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                          >
                            <Navigation className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      )}
                    </div>
                    <span
                      className="px-2 py-0.5 text-xs rounded-full whitespace-nowrap flex-shrink-0"
                      style={{
                        backgroundColor: `${getTgWorkStatusColor(a.tg_status as TgWorkStatus)}20`,
                        color: getTgWorkStatusColor(a.tg_status as TgWorkStatus)
                      }}
                    >
                      {getTgWorkStatusName(a.tg_status as TgWorkStatus)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-nature-500 dark:text-nature-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {format(parseISO(a.start_date), 'EEE', { locale: sv })} {a.start_date}
                    </span>
                    {a.start_time && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {a.start_time.slice(0, 5)}
                      </span>
                    )}
                    {a.coworkers.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Leaf className="h-3.5 w-3.5" />
                        <span className="flex flex-wrap gap-1">
                          {a.coworkers.map(coworker => (
                            <span
                              key={coworker.id}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedCoworker(coworker)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setSelectedCoworker(coworker)
                                }
                              }}
                              className="px-1.5 py-0.5 text-xs rounded-full hover:opacity-80 transition-opacity cursor-pointer"
                              style={{
                                backgroundColor: coworker.color ? `${coworker.color}20` : '#6B728020',
                                color: coworker.color || '#6B7280'
                              }}
                            >
                              {coworker.name}
                            </span>
                          ))}
                        </span>
                      </span>
                    )}
                  </div>
                </button>
              ))}
              {allWeekAssignments.length > MAX_WEEK_ASSIGNMENTS && (
                <button
                  onClick={() => setShowAllWeekAssignments(!showAllWeekAssignments)}
                  className="mt-3 w-full py-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center justify-center gap-1"
                >
                  {showAllWeekAssignments ? 'Visa färre' : `Visa alla (${allWeekAssignments.length})`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* To Report */}
        <div className="bg-white dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-600" />
              Att rapportera
              {toReportAssignments.length > 0 && (
                <span className="text-sm font-normal text-nature-500">({toReportAssignments.length})</span>
              )}
            </h2>
            <button
              onClick={() => navigate('/subcontractor/assignments')}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              Alla uppdrag <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {toReportAssignments.length === 0 ? (
            <p className="text-nature-500 dark:text-nature-400 text-sm py-4 text-center">
              Inga uppdrag att rapportera
            </p>
          ) : (
            <div className="space-y-3">
              {toReportAssignments.map(a => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/subcontractor/assignments/${a.id}`)}
                  className="w-full text-left p-3 rounded-lg border border-nature-200 dark:border-nature-700 hover:bg-nature-50 dark:hover:bg-nature-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      {a.assignment_number && (
                        <span className="text-xs font-mono text-primary-600 dark:text-primary-400">
                          #{a.assignment_number}
                        </span>
                      )}
                      <h4 className="font-medium text-nature-900 dark:text-nature-100 line-clamp-1">
                        {a.title}
                      </h4>
                      {a.address && (
                        <div className="text-sm text-nature-500 dark:text-nature-400 flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="line-clamp-1">{a.address}</span>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a.address)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-1 p-1 rounded hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                          >
                            <Navigation className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      )}
                    </div>
                    <span
                      className="px-2 py-0.5 text-xs rounded-full whitespace-nowrap flex-shrink-0"
                      style={{
                        backgroundColor: `${getTgWorkStatusColor(a.tg_status as TgWorkStatus)}20`,
                        color: getTgWorkStatusColor(a.tg_status as TgWorkStatus)
                      }}
                    >
                      {getTgWorkStatusName(a.tg_status as TgWorkStatus)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-nature-500 dark:text-nature-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {a.start_date}
                    </span>
                    {a.coworkers.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Leaf className="h-3.5 w-3.5" />
                        <span className="flex flex-wrap gap-1">
                          {a.coworkers.map(coworker => (
                            <span
                              key={coworker.id}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedCoworker(coworker)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setSelectedCoworker(coworker)
                                }
                              }}
                              className="px-1.5 py-0.5 text-xs rounded-full hover:opacity-80 transition-opacity cursor-pointer"
                              style={{
                                backgroundColor: coworker.color ? `${coworker.color}20` : '#6B728020',
                                color: coworker.color || '#6B7280'
                              }}
                            >
                              {coworker.name}
                            </span>
                          ))}
                        </span>
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Coworker contact popup */}
      {selectedCoworker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCoworker(null)}>
          <div
            className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                  style={{ backgroundColor: selectedCoworker.color || '#6B7280' }}
                >
                  {selectedCoworker.name.charAt(0)}
                </div>
                <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                  {selectedCoworker.name}
                </h3>
              </div>
              <button
                onClick={() => setSelectedCoworker(null)}
                className="p-1 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-nature-500" />
              </button>
            </div>

            {selectedCoworker.phone ? (
              <a
                href={`tel:${selectedCoworker.phone}`}
                className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
              >
                <Phone className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <span className="text-primary-600 dark:text-primary-400 font-medium">
                  {selectedCoworker.phone}
                </span>
              </a>
            ) : (
              <p className="text-nature-500 dark:text-nature-400 text-sm">
                Inget telefonnummer registrerat
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
